// Backend
import { BASE_URL } from '../backend';
import mockHandlers from '../backend/mockHandlers';
import { retrieveRequestId } from '../reducers/auth/authAuthorizeHelpers';
import { 
  AUTH_SERVER_ERROR,
  AUTH_TIMEOUT_MODAL_OPEN 
} from '../reducers/auth/authActions';
import { isSessionTimeoutError } from './sessionErrors';

// Set to true to use mock data, false to use real API
const USE_MOCK = false;

const isServerError = ({ response }) => {
  if (!response) return false;
  return response.status >= 500 || response.status === 404;
};

// For verifyConsent, 404 is a valid response (no consent exists), not an error
const isExpected404 = (url, status) => {
  return status === 404 && url === "/verifyConsent";
};

// The API middleware intercepts any action that looks like a request descriptor
// (has url + APIMethod) and handles it directly, returning { body, response }.
// Regular Redux actions pass through unchanged via next(action).
const apiMiddleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    if (!action.url || !action.APIMethod) return next(action);

    const handlerKey = `${action.APIMethod} ${action.url}`;

    if (USE_MOCK && mockHandlers[handlerKey]) {
      const mock = mockHandlers[handlerKey]({
        body: action.body,
        headers: action.headers,
        params: action.params,
      });
      const payload = {
        body: mock.data,
        response: { ok: mock.ok, status: mock.status },
      };

      // Dispatch server error for 500+/404, except for endpoints where 404 is expected
      if (isServerError(payload) && !isExpected404(action.url, mock.status)) {
        dispatch({ type: AUTH_SERVER_ERROR, payload });
      }

      return Promise.resolve(payload);
    }
    const queryString = action.params
      ? new URLSearchParams(action.params).toString()
      : '';
    const resolvedBaseUrl = action.baseUrl || BASE_URL;
    const url = `${resolvedBaseUrl}${action.url}${queryString ? `?${queryString}` : ''}`;

    const requestId = retrieveRequestId();
    const headers = {
      ...(action.body ? { 'Content-Type': 'application/json' } : {}),
      ...(requestId ? { 'x-req-id': requestId } : {}),
      ...action.headers,
    };

    return fetch(url, {
      method: action.APIMethod,
      headers,
      body: action.body ? JSON.stringify(action.body) : undefined,
      credentials: "include",
    })
      .then(async (response) => {
        const body = await response.json().catch(() => ({}));
        const payload = {
          body,
          response: {
            ok: response.ok,
            status: response.status,
            url: response.url,
          },
        };

        if (isSessionTimeoutError(body)) {
          dispatch({ type: AUTH_TIMEOUT_MODAL_OPEN });
          return payload;
        }

        // Dispatch server error for 500+/404, except for endpoints where 404 is expected
        if (
          isServerError(payload) &&
          !isExpected404(action.url, response.status) &&
          !isSessionTimeoutError(body)
        ) {
          dispatch({ type: AUTH_SERVER_ERROR, payload });
        }

        return payload;
      })
      .catch((error) => {
        return Promise.reject({ error });
      });
  };

export default apiMiddleware;
 